import React from 'react'
import {
  XMarkIcon,
  CheckIcon,
  CreditCardIcon,
  UsersIcon,
  GlobeAltIcon,
  LinkIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  CogIcon,
} from '@heroicons/react/24/outline'
import { Plan } from '../types/plan.types'

interface PlanPreviewProps {
  isOpen: boolean
  onClose: () => void
  plan: Plan | null
}

const PlanPreview: React.FC<PlanPreviewProps> = ({ isOpen, onClose, plan }) => {
  if (!isOpen || !plan) {
    return null
  }

  const formatPrice = (price: number) => {
    return price === 0 ? 'Free' : `₩${price}`
  }

  const formatFeatureValue = (value: number | boolean) => {
    if (typeof value === 'boolean') {
      return value ? 'Included' : 'Not Included'
    }
    if (value === -1) {
      return 'Unlimited'
    }
    if (value === 0) {
      return 'Not Included'
    }
    return value.toLocaleString()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl dark:bg-gray-800">
        {/* Header */}
        <div className="sticky top-0 rounded-t-2xl border-b border-gray-200 bg-white px-6 py-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {plan.color && (
                <div
                  className="h-6 w-6 rounded-full"
                  style={{ backgroundColor: plan.color }}
                />
              )}
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {plan.displayName}
                  {plan.badge && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      {plan.badge}
                    </span>
                  )}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Plan Details Preview
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Plan Overview */}
          <div className="mb-8 text-center">
            <div className="mb-4 flex items-center justify-center space-x-4">
              {plan.color && (
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-full"
                  style={{ backgroundColor: `${plan.color}20` }}
                >
                  <CreditCardIcon
                    className="h-6 w-6"
                    style={{ color: plan.color }}
                  />
                </div>
              )}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {plan.displayName}
                </h3>
                <p className="mt-1 text-gray-600 dark:text-gray-400">
                  {plan.description}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center space-x-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900 dark:text-white">
                  {formatPrice(plan.price.monthly)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  per month
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900 dark:text-white">
                  {formatPrice(plan.price.yearly)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  per year
                </div>
                {plan.price.yearlyDiscount && (
                  <div className="text-xs font-medium text-green-600 dark:text-green-400">
                    Save {plan.price.yearlyDiscount}%
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Core Features */}
          <div className="mb-8">
            <h4 className="mb-4 flex items-center text-lg font-semibold text-gray-900 dark:text-white">
              <ChartBarIcon className="mr-2 h-5 w-5" />
              Core Features
            </h4>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <LinkIcon className="mr-2 h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Links per Month
                    </span>
                  </div>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {formatFeatureValue(plan.features.linksPerMonth)}
                  </span>
                </div>
              </div>

              <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <GlobeAltIcon className="mr-2 h-5 w-5 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Custom Domains
                    </span>
                  </div>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {formatFeatureValue(plan.features.customDomains)}
                  </span>
                </div>
              </div>

              <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <UsersIcon className="mr-2 h-5 w-5 text-purple-600 dark:text-purple-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Team Members
                    </span>
                  </div>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {formatFeatureValue(plan.features.teamMembers)}
                  </span>
                </div>
              </div>

              <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <ChartBarIcon className="mr-2 h-5 w-5 text-orange-600 dark:text-orange-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Clicks per Month
                    </span>
                  </div>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {formatFeatureValue(plan.features.clicksPerMonth)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Advanced Features */}
          {(plan.features.whiteLabel ||
            plan.features.apiAccess ||
            plan.features.prioritySupport ||
            plan.features.analytics) && (
            <div className="mb-6">
              <h4 className="mb-4 flex items-center text-lg font-semibold text-gray-900 dark:text-white">
                <CogIcon className="mr-2 h-5 w-5" />
                Advanced Features
              </h4>
              <div className="space-y-3">
                {plan.features.whiteLabel && (
                  <div className="flex items-center">
                    <CheckIcon className="mr-3 h-5 w-5 text-green-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      White-label & Custom Branding
                    </span>
                  </div>
                )}
                {plan.features.apiAccess && (
                  <div className="flex items-center">
                    <CheckIcon className="mr-3 h-5 w-5 text-green-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      API Access & Integrations
                    </span>
                  </div>
                )}
                {plan.features.prioritySupport && (
                  <div className="flex items-center">
                    <CheckIcon className="mr-3 h-5 w-5 text-green-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Priority Customer Support
                    </span>
                  </div>
                )}
                {plan.features.analytics && (
                  <div className="flex items-center">
                    <CheckIcon className="mr-3 h-5 w-5 text-green-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Advanced Analytics & Reporting
                    </span>
                  </div>
                )}
                {plan.features.advancedSecurity && (
                  <div className="flex items-center">
                    <CheckIcon className="mr-3 h-5 w-5 text-green-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Advanced Security Features
                    </span>
                  </div>
                )}
                {plan.features.webhooks && (
                  <div className="flex items-center">
                    <CheckIcon className="mr-3 h-5 w-5 text-green-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Webhooks & Real-time Events
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Plan Status */}
          <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <ShieldCheckIcon className="mr-2 h-5 w-5 text-gray-600 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Plan Status
                </span>
              </div>
              <div className="flex items-center">
                <div
                  className={`mr-2 h-2 w-2 rounded-full ${
                    plan.isActive ? 'bg-green-500' : 'bg-red-500'
                  }`}
                />
                <span
                  className={`text-sm font-medium ${
                    plan.isActive
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}
                >
                  {plan.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            {plan.createdAt && (
              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Created
                </span>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {new Date(plan.createdAt).toLocaleDateString()}
                </span>
              </div>
            )}

            {plan.updatedAt && (
              <div className="mt-1 flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Last Updated
                </span>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {new Date(plan.updatedAt).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 rounded-b-2xl border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-600 dark:bg-gray-700">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-500 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PlanPreview
